import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useBackgroundAudio from '../hooks/useBackgroundAudio';

// Emotion words organized by category - 90 total
const EMOTION_CATEGORIES = {
  pressureTension: [
    'tension', 'strain', 'pressure', 'tightness', 'constriction',
    'overload', 'compression', 'friction', 'bracing'
  ],
  fatigueDrain: [
    'fatigue', 'exhaustion', 'burnout', 'weariness', 'heaviness',
    'thinning', 'depletion', 'erosion', 'numbness'
  ],
  resistanceAvoidance: [
    'resistance', 'reluctance', 'avoidance', 'withdrawal', 'hesitation',
    'refusal', 'shutdown', 'stalling', 'deflection'
  ],
  angerDisruption: [
    'anger', 'rage', 'fury', 'irritation', 'resentment',
    'bitterness', 'hostility', 'spite', 'hate'
  ],
  hurtViolation: [
    'hurt', 'wounded', 'betrayed', 'violated', 'exposed',
    'used', 'dismissed', 'erased', 'undermined'
  ],
  fearAlarm: [
    'fear', 'panic', 'dread', 'unease', 'anxiety',
    'alarm', 'hypervigilance', 'threat', 'freeze'
  ],
  chaosOverwhelm: [
    'confusion', 'noise', 'static', 'overwhelm', 'scatter',
    'flooded', 'ahhhhhh', 'spiral'
  ],
  selfProtection: [
    'guardedness', 'defensiveness', 'caution', 'shielding', 'distance',
    'reserve', 'containment', 'pullback', 'hardening'
  ],
  clarityStability: [
    'clarity', 'resolve', 'steadiness', 'alignment', 'grounding',
    'coherence', 'calm', 'certainty', 'centered'
  ],
  integrityValue: [
    'dignity', 'self-respect', 'autonomy', 'truth', 'ownership',
    'priority', 'limits', 'preservation', 'enough'
  ]
};

// Get all category names
const CATEGORY_NAMES = Object.keys(EMOTION_CATEGORIES);

// Flatten all emotions for initial pool
const getAllEmotions = () => {
  const all = [];
  Object.entries(EMOTION_CATEGORIES).forEach(([category, words]) => {
    words.forEach(word => {
      all.push({ word, category });
    });
  });
  return all;
};

const FeelInteractive = ({ onExit }) => {
  const [phase, setPhase] = useState(0);
  // Phase 0: Black field (1-2 sec)
  // Phase 1: Microcopy 1 "Stay with what is present" (fades after 2 sec)
  // Phase 2: Bubbles begin rising

  const [bubbles, setBubbles] = useState([]);
  const [availableEmotions, setAvailableEmotions] = useState(() => getAllEmotions());
  const [usedCategories, setUsedCategories] = useState([]);
  const [touchedCount, setTouchedCount] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showMicrocopy2, setShowMicrocopy2] = useState(false);
  const [showMicrocopy3, setShowMicrocopy3] = useState(false);
  const [isSettling, setIsSettling] = useState(false);
  const [showExitOptions, setShowExitOptions] = useState(false);
  const [ripples, setRipples] = useState([]);
  
  const bubbleIdRef = useRef(0);
  const containerRef = useRef(null);
  const lastCategoryRef = useRef(null);

  // Subtle background audio
  useBackgroundAudio(true);

  // Get next emotion - avoiding same category back-to-back
  const getNextEmotion = useCallback(() => {
    if (availableEmotions.length === 0) return null;
    
    // Filter out same category as last used
    let candidates = availableEmotions;
    if (lastCategoryRef.current && availableEmotions.length > 1) {
      candidates = availableEmotions.filter(e => e.category !== lastCategoryRef.current);
      // If all remaining are same category, just use any
      if (candidates.length === 0) {
        candidates = availableEmotions;
      }
    }
    
    // Pick random from candidates
    const index = Math.floor(Math.random() * candidates.length);
    const selected = candidates[index];
    
    // Remove from available pool
    setAvailableEmotions(prev => prev.filter(e => e.word !== selected.word));
    lastCategoryRef.current = selected.category;
    
    return selected.word;
  }, [availableEmotions]);

  // Check if two bubbles overlap more than allowed percentage
  const checkOverlap = (bubble1, bubble2, maxOverlapPercent = 20) => {
    // Calculate horizontal distance between bubble centers (as percentage of screen)
    const distance = Math.abs(bubble1.startX - bubble2.startX);
    
    // Calculate the combined radius as percentage (assuming 1920px screen width)
    // Bubble size is 70-100px, so each bubble is ~4-5% of screen width
    const avgSizePercent = ((bubble1.size + bubble2.size) / 2) / 19.2;
    
    // If distance is less than combined radii minus allowed overlap, they overlap too much
    const minAllowedDistance = avgSizePercent * (1 - maxOverlapPercent / 100);
    
    return distance < minAllowedDistance;
  };

  // Find a valid X position that doesn't overlap too much with existing bubbles
  const findValidPosition = (size, existingBubbles, maxAttempts = 10) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const startX = 8 + Math.random() * 84; // 8-92% from left
      
      // Check against all existing bubbles (only those still near bottom)
      let hasOverlap = false;
      for (const existing of existingBubbles) {
        // Only check bubbles that are still in the lower portion of the screen
        // (bubbles that have risen too high won't collide with new spawns)
        const testBubble = { startX, size };
        if (checkOverlap(testBubble, existing)) {
          hasOverlap = true;
          break;
        }
      }
      
      if (!hasOverlap) {
        return startX;
      }
    }
    
    // If no valid position found, return null to skip this spawn
    return null;
  };

  // Generate a random bubble with collision avoidance
  const createBubble = useCallback((existingBubbles) => {
    const emotion = getNextEmotion();
    if (!emotion) return null;
    
    const size = 70 + Math.random() * 30; // 70-100px - smaller bubble boundaries
    const startX = findValidPosition(size, existingBubbles);
    
    // Skip if no valid position found
    if (startX === null) return null;
    
    const speed = 10 + Math.random() * 8; // 10-18 seconds to rise (faster)
    const delay = Math.random() * 0.5; // 0-0.5 second delay
    
    bubbleIdRef.current += 1;
    
    return {
      id: bubbleIdRef.current,
      emotion,
      size,
      startX,
      speed,
      delay,
      touched: false,
      spawnTime: Date.now()
    };
  }, [getNextEmotion]);

  // Phase progression
  useEffect(() => {
    const timers = [];
    
    // Phase 0 -> 1: After 1.5 seconds, show first microcopy
    timers.push(setTimeout(() => setPhase(1), 1500));
    
    // Phase 1 -> 2: After microcopy fades (2 more seconds), start bubbles
    timers.push(setTimeout(() => setPhase(2), 4000));
    
    // Microcopy 2: After 6 seconds of bubbles (if no interaction)
    timers.push(setTimeout(() => {
      if (!hasInteracted) {
        setShowMicrocopy2(true);
        setTimeout(() => setShowMicrocopy2(false), 3000);
      }
    }, 10000));
    
    // Settling and exit: After ~50 seconds total
    timers.push(setTimeout(() => {
      setIsSettling(true);
    }, 45000));
    
    timers.push(setTimeout(() => {
      setShowExitOptions(true);
    }, 55000));
    
    return () => timers.forEach(t => clearTimeout(t));
  }, [hasInteracted]);

  // Generate bubbles periodically - 3-4 at a time
  useEffect(() => {
    if (phase < 2) return;
    
    const generateBubble = () => {
      setBubbles(prev => {
        // Allow 3-4 bubbles at a time
        if (prev.length >= 4) return prev;
        if (isSettling && prev.length >= 2) return prev;
        
        // Filter to only check recent bubbles (spawned in last 3 seconds) for collision
        const now = Date.now();
        const recentBubbles = prev.filter(b => (now - b.spawnTime) < 3000);
        
        const newBubble = createBubble(recentBubbles);
        if (!newBubble) return prev;
        
        // Schedule removal when bubble goes off screen
        setTimeout(() => {
          setBubbles(current => current.filter(b => b.id !== newBubble.id));
        }, (newBubble.speed + newBubble.delay) * 1000 + 1000);
        
        return [...prev, newBubble];
      });
    };
    
    // Start with first bubble quickly
    const firstTimeout = setTimeout(generateBubble, 1000);
    
    // Spawn new bubbles more frequently - every 2-3 seconds
    // Only spawns when there's room (less than 4 bubbles on screen)
    const interval = setInterval(() => {
      generateBubble();
    }, isSettling ? 5000 : 2000 + Math.random() * 1000);
    
    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, [phase, isSettling, createBubble]);

  // Handle bubble touch
  const handleBubbleTouch = useCallback((bubble, e) => {
    e.stopPropagation();
    
    if (bubble.touched) return;
    
    // Mark as touched
    setBubbles(prev => prev.map(b => 
      b.id === bubble.id ? { ...b, touched: true } : b
    ));
    
    // Create ripple effect
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (containerRect) {
      const ripple = {
        id: Date.now(),
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top + rect.height / 2
      };
      setRipples(prev => [...prev, ripple]);
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== ripple.id));
      }, 2000);
    }
    
    // Remove bubble after animation
    setTimeout(() => {
      setBubbles(prev => prev.filter(b => b.id !== bubble.id));
    }, 800);
    
    // Track interaction
    if (!hasInteracted) {
      setHasInteracted(true);
      setShowMicrocopy2(true);
      setTimeout(() => setShowMicrocopy2(false), 3000);
    }
    
    setTouchedCount(prev => {
      const newCount = prev + 1;
      
      // Show microcopy 3 after 4-7 bubbles touched
      if (newCount >= 4 && newCount <= 7 && !showMicrocopy3) {
        setTimeout(() => {
          setShowMicrocopy3(true);
          setTimeout(() => setShowMicrocopy3(false), 4000);
        }, 1500);
      }
      
      // Trigger settling after enough interaction
      if (newCount >= 8 && !isSettling) {
        setTimeout(() => setIsSettling(true), 4000);
        setTimeout(() => setShowExitOptions(true), 10000);
      }
      
      return newCount;
    });
    
    // Haptic feedback
    if ('vibrate' in navigator) {
      navigator.vibrate(5);
    }
  }, [hasInteracted, showMicrocopy3, isSettling]);

  // Handle exit selection
  const handleExit = useCallback((choice) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
    onExit(choice);
  }, [onExit]);

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 overflow-hidden"
      style={{ backgroundColor: 'hsl(240, 8%, 3%)' }}
    >
      {/* Deepening background during settling */}
      <motion.div
        className="absolute inset-0"
        animate={{
          backgroundColor: isSettling ? 'hsl(240, 10%, 2%)' : 'hsl(240, 8%, 3%)'
        }}
        transition={{ duration: 6 }}
      />

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <motion.div
          key={ripple.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            width: 10,
            height: 10,
            marginLeft: -5,
            marginTop: -5,
            border: '1px solid hsl(40, 15%, 35%)'
          }}
          initial={{ scale: 1, opacity: 0.4 }}
          animate={{ scale: 18, opacity: 0 }}
          transition={{ duration: 2.5, ease: 'easeOut' }}
        />
      ))}

      {/* Microcopy 1: Entry acknowledgment */}
      <AnimatePresence>
        {phase === 1 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.45 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-base sm:text-lg italic text-center"
            style={{ color: 'hsl(40, 10%, 50%)' }}
          >
            Stay with what is present.
          </motion.p>
        )}
      </AnimatePresence>

      {/* Bubbles */}
      <AnimatePresence>
        {bubbles.map(bubble => (
          <motion.button
            key={bubble.id}
            onClick={(e) => handleBubbleTouch(bubble, e)}
            className="absolute rounded-full flex items-center justify-center cursor-pointer border-0 bg-transparent p-2"
            style={{
              width: bubble.size,
              height: bubble.size,
              left: `${bubble.startX}%`,
              marginLeft: -bubble.size / 2
            }}
            initial={{ 
              bottom: -bubble.size,
              opacity: 0,
              scale: 0.8
            }}
            animate={bubble.touched ? {
              scale: 1.4,
              opacity: 0
            } : {
              bottom: '105%',
              opacity: [0, 0.75, 0.75, 0],
              scale: 1
            }}
            transition={bubble.touched ? {
              duration: 0.8,
              ease: 'easeOut'
            } : {
              duration: bubble.speed,
              delay: bubble.delay,
              ease: 'linear',
              opacity: {
                duration: bubble.speed,
                times: [0, 0.08, 0.88, 1]
              }
            }}
          >
            {/* Bubble visual */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                border: `1px solid hsl(40, 12%, ${bubble.touched ? '55%' : '28%'})`,
                background: 'radial-gradient(circle at 30% 30%, hsl(40, 8%, 12%, 0.25), transparent)'
              }}
              animate={bubble.touched ? {
                borderColor: 'hsl(40, 18%, 50%)'
              } : {}}
            />
            
            {/* Emotion word - larger text for legibility */}
            <motion.span
              className="font-display text-lg sm:text-xl md:text-2xl tracking-wide relative z-10 px-3"
              style={{ 
                color: bubble.touched ? 'hsl(40, 15%, 82%)' : 'hsl(40, 12%, 58%)'
              }}
              animate={bubble.touched ? {
                color: 'hsl(40, 20%, 88%)',
                scale: 1.15
              } : {}}
              transition={{ duration: 0.3 }}
            >
              {bubble.emotion}
            </motion.span>
          </motion.button>
        ))}
      </AnimatePresence>

      {/* Microcopy 2: Permission */}
      <AnimatePresence>
        {showMicrocopy2 && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.38 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute bottom-28 left-1/2 -translate-x-1/2 font-display text-sm sm:text-base italic text-center"
            style={{ color: 'hsl(40, 10%, 48%)' }}
          >
            Nothing needs to be caught.
          </motion.p>
        )}
      </AnimatePresence>

      {/* Microcopy 3: Recognition */}
      <AnimatePresence>
        {showMicrocopy3 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.45 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-display text-base sm:text-lg italic text-center"
            style={{ color: 'hsl(40, 12%, 52%)' }}
          >
            <p>Some feelings pass.</p>
            <p className="mt-3">Some ask for a line.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Exit options */}
      <AnimatePresence>
        {showExitOptions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2.5 }}
            className="absolute bottom-12 left-0 right-0 flex justify-center gap-12 sm:gap-16 md:gap-20"
          >
            {['Weigh', 'Translate', 'Close'].map((option, index) => (
              <motion.button
                key={option}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.4, duration: 1.8 }}
                onClick={() => handleExit(option.toLowerCase())}
                className="font-display text-lg sm:text-xl tracking-[0.12em] transition-all duration-700 hover:tracking-[0.18em]"
                style={{ color: 'hsl(40, 10%, 52%)' }}
              >
                {option}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FeelInteractive;
